import React, { useEffect, useRef, useState } from 'react';
import { AppState } from '../store';
import { connect } from 'react-redux';
import { postSendEmailSolicitudBusinessProject } from '../store/MsMessages/thunks';
import { Link } from 'react-router-dom';
import { useParallaxMotion } from './useParallaxMotion';

interface TableProps {

  postSendEmailSolicitudBusinessProject?: any;

}

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:ital,wght@0,300;0,400;0,500;1,300&family=Syne:wght@400;700;800&display=swap');

  .tbc-root *, .tbc-root *::before, .tbc-root *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .tbc-root {
    --bg: #f5f4f0;
    --ink: #0a0a0a;
    --blue: #1a3cff;
    --blue-light: #e8ecff;
    --mono: 'DM Mono', monospace;
    --sans: 'Syne', sans-serif;
    --border: rgba(10,10,10,0.12);
    --motion-y-soft: 0px;
    --motion-y-mid: 0px;
    --motion-y-deep: 0px;
    --motion-y-forward: 0px;
    --motion-pointer-x: 0px;
    --motion-pointer-y: 0px;
    --motion-pointer-x-invert: 0px;
    --motion-pointer-y-invert: 0px;
    --motion-rotate-soft: 0deg;
    --motion-tilt: 0deg;
    font-family: var(--sans);
    background: var(--bg);
    color: var(--ink);
    overflow-x: hidden;
  }

  /* Nav */
  .tbc-nav {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
    padding: 1.6rem 3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(245,244,240,0.88);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
  }

  .tbc-logo {
    font-family: var(--mono);
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--ink);
    text-decoration: none;
  }

  .tbc-logo span { color: var(--blue); }

  .tbc-nav-links {
    display: flex;
    gap: 2.5rem;
    list-style: none;
  }

  .tbc-nav-links a {
    font-family: var(--mono);
    font-size: 0.78rem;
    color: var(--ink);
    text-decoration: none;
    letter-spacing: 0.04em;
    opacity: 0.55;
    transition: opacity 0.2s;
  }

  .tbc-nav-links a:hover { opacity: 1; }

  .tbc-nav-actions {
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  .tbc-nav-cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 42px;
    font-family: var(--mono);
    font-size: 0.78rem;
    letter-spacing: 0.04em;
    padding: 0.55rem 1.4rem;
    background: var(--blue);
    color: #fff;
    border: 1px solid var(--blue);
    cursor: pointer;
    transition: background 0.2s, color 0.2s, border-color 0.2s, transform 0.2s;
    text-decoration: none;
    white-space: nowrap;
  }

  .tbc-nav-cta:hover {
    background: #0026cc;
    border-color: #0026cc;
    transform: translateY(-1px);
  }

  .tbc-nav-secondary {
    background: transparent;
    color: var(--blue);
    border: 1px solid var(--blue);
  }

  .tbc-nav-secondary:hover {
    background: var(--blue);
    color: #fff;
    border-color: var(--blue);
  }

  /* Hero */
  .tbc-hero {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 0 3rem 5rem;
    position: relative;
    overflow: hidden;
  }

  .tbc-hero-bg {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 0;
    transform: translate(
      calc(-50% + var(--motion-pointer-x-invert)),
      calc(-50% + var(--motion-y-deep))
    ) rotate(var(--motion-rotate-soft));
    font-family: var(--sans);
    font-weight: 800;
    font-size: 38vw;
    color: transparent;
    -webkit-text-stroke: 1px rgba(26,60,255,0.06);
    user-select: none;
    pointer-events: none;
    letter-spacing: -0.05em;
  }

  .tbc-parallax-stage {
    position: absolute;
    inset: 0;
    z-index: 0;
    overflow: hidden;
    pointer-events: none;
  }

  .tbc-layer {
    position: absolute;
    display: block;
    border: 1px solid rgba(26,60,255,0.16);
    will-change: transform;
  }

  .tbc-layer-grid {
    top: 17vh;
    right: 8vw;
    width: min(24vw, 330px);
    min-width: 220px;
    aspect-ratio: 1;
    background-image:
      linear-gradient(rgba(26,60,255,0.09) 1px, transparent 1px),
      linear-gradient(90deg, rgba(26,60,255,0.09) 1px, transparent 1px);
    background-size: 28px 28px;
    transform: translate3d(var(--motion-pointer-x), var(--motion-y-mid), 0) rotate(8deg);
  }

  .tbc-layer-panel {
    left: 8vw;
    top: 28vh;
    width: 9rem;
    height: 9rem;
    background:
      linear-gradient(90deg, transparent 48%, rgba(26,60,255,0.18) 48% 52%, transparent 52%),
      rgba(26,60,255,0.04);
    transform: translate3d(var(--motion-pointer-x-invert), var(--motion-y-soft), 0) rotate(-12deg);
  }

  .tbc-layer-rail {
    right: 16vw;
    bottom: 16vh;
    width: min(42vw, 560px);
    height: 1px;
    background: rgba(26,60,255,0.32);
    border: 0;
    transform: translate3d(var(--motion-pointer-x-invert), var(--motion-y-forward), 0);
  }

  .tbc-layer-rail::before,
  .tbc-layer-rail::after {
    content: '';
    position: absolute;
    top: -5px;
    width: 11px;
    height: 11px;
    border: 1px solid rgba(26,60,255,0.36);
    background: var(--bg);
  }

  .tbc-layer-rail::before { left: 0; }
  .tbc-layer-rail::after { right: 0; }

  .tbc-hero-label,
  .tbc-hero-title,
  .tbc-hero-bottom {
    position: relative;
    z-index: 1;
  }

  .tbc-hero-label {
    font-family: var(--mono);
    font-size: 0.75rem;
    letter-spacing: 0.12em;
    opacity: 0.45;
    text-transform: uppercase;
    margin-bottom: 2rem;
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  .tbc-hero-label::before {
    content: '';
    display: inline-block;
    width: 2rem;
    height: 1px;
    background: var(--blue);
    opacity: 0.6;
  }

  .tbc-hero-title {
    font-family: var(--sans);
    font-weight: 800;
    font-size: clamp(3.5rem, 9vw, 8.5rem);
    line-height: 0.92;
    letter-spacing: -0.04em;
    max-width: 14ch;
    animation: tbc-slideUp 0.9s cubic-bezier(0.16,1,0.3,1) 0.2s both;
  }

  .tbc-hero-title .tbc-accent { color: var(--blue); }

  .tbc-hero-bottom {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-top: 3.5rem;
    gap: 2rem;
    animation: tbc-slideUp 0.9s cubic-bezier(0.16,1,0.3,1) 0.45s both;
  }

  .tbc-hero-desc {
    font-family: var(--mono);
    font-size: 0.88rem;
    line-height: 1.8;
    opacity: 0.6;
    max-width: 32ch;
  }

  .tbc-hero-btn {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-family: var(--mono);
    font-size: 0.82rem;
    letter-spacing: 0.05em;
    color: var(--ink);
    text-decoration: none;
    border-bottom: 1px solid currentColor;
    padding-bottom: 0.3rem;
    transition: color 0.2s, gap 0.2s;
  }

  .tbc-hero-btn:hover {
    color: var(--blue);
    gap: 1.6rem;
  }

  /* Ticker */
  .tbc-ticker-wrap {
    overflow: hidden;
    padding: 1rem 0;
    background: var(--blue);
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
  }

  .tbc-ticker {
    display: flex;
    width: max-content;
    animation: tbc-ticker 18s linear infinite;
  }

  .tbc-ticker-item {
    white-space: nowrap;
    font-family: var(--mono);
    font-size: 0.78rem;
    letter-spacing: 0.08em;
    color: #fff;
    padding: 0 2.5rem;
    opacity: 0.85;
  }

  .tbc-ticker-item span {
    opacity: 0.4;
    margin: 0 1.5rem;
  }

  /* Sections */
  .tbc-section {
    position: relative;
    padding: 7rem 3rem;
  }

  .tbc-section-label {
    font-family: var(--mono);
    font-size: 0.72rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    opacity: 0.4;
    margin-bottom: 4rem;
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  .tbc-section-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
    max-width: 6rem;
  }

  /* Services */
  .tbc-services-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    border-top: 1px solid var(--border);
  }

  .tbc-card {
    padding: 3rem 2.5rem;
    border-right: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
    position: relative;
    transition: background 0.3s;
  }

  .tbc-card:nth-child(3n) { border-right: none; }

  .tbc-card:hover { background: var(--blue-light); }

  .tbc-card-num {
    font-family: var(--mono);
    font-size: 0.7rem;
    letter-spacing: 0.1em;
    opacity: 0.3;
    margin-bottom: 2rem;
    display: block;
  }

  .tbc-card-icon {
    font-size: 1.6rem;
    margin-bottom: 1.5rem;
    display: block;
  }

  .tbc-card-name {
    font-family: var(--sans);
    font-weight: 700;
    font-size: 1.25rem;
    margin-bottom: 1rem;
    letter-spacing: -0.02em;
  }

  .tbc-card-desc {
    font-family: var(--mono);
    font-size: 0.78rem;
    line-height: 1.9;
    opacity: 0.55;
  }

  /* About */
  .tbc-about {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6rem;
    align-items: center;
    border-top: 1px solid var(--border);
  }

  .tbc-about-title {
    font-size: clamp(2.4rem, 5vw, 4rem);
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.04em;
    margin-bottom: 2.5rem;
  }

  .tbc-about-title .tbc-accent { color: var(--blue); }

  .tbc-about-text {
    font-family: var(--mono);
    font-size: 0.85rem;
    line-height: 2;
    opacity: 0.6;
    margin-bottom: 2.5rem;
  }

  .tbc-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
    border-top: 1px solid var(--border);
    padding-top: 2.5rem;
  }

  .tbc-stat-val {
    font-size: 2.8rem;
    font-weight: 800;
    letter-spacing: -0.04em;
    color: var(--blue);
    line-height: 1;
  }

  .tbc-stat-label {
    font-family: var(--mono);
    font-size: 0.72rem;
    opacity: 0.45;
    letter-spacing: 0.06em;
    margin-top: 0.4rem;
    text-transform: uppercase;
  }

  .tbc-about-visual {
    position: relative;
    height: 420px;
    background: var(--blue);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .tbc-about-visual::before,
  .tbc-about-visual::after {
    content: '';
    position: absolute;
    pointer-events: none;
    will-change: transform;
  }

  .tbc-about-visual::before {
    inset: -20%;
    background-image:
      linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px);
    background-size: 34px 34px;
    transform: translate3d(var(--motion-pointer-x), var(--motion-y-soft), 0) rotate(4deg);
  }

  .tbc-about-visual::after {
    right: 16%;
    top: 14%;
    width: 16rem;
    height: 16rem;
    border: 1px solid rgba(255,255,255,0.2);
    transform: translate3d(var(--motion-pointer-x-invert), var(--motion-y-forward), 0) rotate(-10deg);
  }

  .tbc-about-code {
    position: relative;
    z-index: 1;
    font-family: var(--mono);
    font-size: 0.78rem;
    line-height: 1.9;
    color: rgba(255,255,255,0.6);
    padding: 2rem;
    font-style: italic;
    transform: translate3d(var(--motion-pointer-x-invert), var(--motion-pointer-y-invert), 0);
  }

  .tbc-kw { color: #8ab4ff; }
  .tbc-fn { color: #fff; font-style: normal; font-weight: 500; }
  .tbc-str { color: rgba(255,255,255,0.35); }

  /* Contact */
  .tbc-contact {
    position: relative;
    background: var(--ink);
    color: #f5f4f0;
    border-top: 1px solid var(--border);
    overflow: hidden;
  }

  .tbc-contact::before {
    content: '';
    position: absolute;
    right: -8rem;
    top: 3rem;
    width: 28rem;
    height: 28rem;
    border: 1px solid rgba(26,60,255,0.28);
    background-image: repeating-linear-gradient(
      90deg,
      rgba(26,60,255,0.14) 0 1px,
      transparent 1px 24px
    );
    pointer-events: none;
    transform: translate3d(var(--motion-pointer-x-invert), var(--motion-y-soft), 0) rotate(11deg);
    will-change: transform;
  }

  .tbc-contact-wrap {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 0.95fr 1.05fr;
    gap: 4rem;
    align-items: start;
  }

  .tbc-contact .tbc-section-label {
    color: #f5f4f0;
    opacity: 0.35;
  }

  .tbc-contact-title {
    font-size: clamp(3rem, 7vw, 6rem);
    font-weight: 800;
    letter-spacing: -0.04em;
    line-height: 0.95;
    margin-bottom: 1.8rem;
  }

  .tbc-contact-title .tbc-accent { color: var(--blue); }

  .tbc-contact-desc {
    font-family: var(--mono);
    font-size: 0.85rem;
    line-height: 1.9;
    opacity: 0.5;
    max-width: 38ch;
  }

  .tbc-contact-card {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    padding: 2rem;
  }

  .tbc-contact-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .tbc-form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .tbc-field {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }

  .tbc-field label {
    font-family: var(--mono);
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0.7;
  }

  .tbc-input,
  .tbc-textarea {
    width: 100%;
    border: 1px solid rgba(255,255,255,0.12);
    background: rgba(255,255,255,0.03);
    color: #f5f4f0;
    padding: 0.95rem 1rem;
    font-family: var(--mono);
    font-size: 0.84rem;
    outline: none;
    transition: border-color 0.2s, background 0.2s;
  }

  .tbc-input::placeholder,
  .tbc-textarea::placeholder {
    color: rgba(245,244,240,0.35);
  }

  .tbc-input:focus,
  .tbc-textarea:focus {
    border-color: var(--blue);
    background: rgba(255,255,255,0.05);
  }

  .tbc-textarea {
    min-height: 160px;
    resize: vertical;
  }

  .tbc-form-note {
    font-family: var(--mono);
    font-size: 0.74rem;
    opacity: 0.45;
    line-height: 1.7;
  }

  .tbc-submit {
    display: inline-block;
    width: fit-content;
    padding: 1rem 2rem;
    background: var(--blue);
    color: #fff;
    border: none;
    cursor: pointer;
    font-family: var(--mono);
    font-size: 0.82rem;
    letter-spacing: 0.06em;
    transition: background 0.2s, transform 0.2s, opacity 0.2s;
  }

  .tbc-submit:hover {
    background: #3358ff;
    transform: translateY(-2px);
  }

  .tbc-submit:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  .tbc-success {
    font-family: var(--mono);
    font-size: 0.8rem;
    color: #97f3b1;
    margin-top: 0.25rem;
  }

  .tbc-error {
    font-family: var(--mono);
    font-size: 0.8rem;
    color: #ff9b9b;
    margin-top: 0.25rem;
  }

  /* Footer */
  .tbc-footer {
    border-top: 1px solid rgba(255,255,255,0.08);
    padding: 2.5rem 3rem;
    background: var(--ink);
    color: #f5f4f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }

  .tbc-footer .tbc-logo {
    color: #f5f4f0;
    font-size: 0.85rem;
  }

  .tbc-copy {
    font-family: var(--mono);
    font-size: 0.7rem;
    opacity: 0.3;
    letter-spacing: 0.06em;
  }

  .tbc-footer-links {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.7rem 1.2rem;
  }

  .tbc-footer-links a {
    font-family: var(--mono);
    font-size: 0.68rem;
    color: #f5f4f0;
    letter-spacing: 0.04em;
    text-decoration: none;
    opacity: 0.45;
    transition: color 0.2s, opacity 0.2s;
  }

  .tbc-footer-links a:hover {
    color: var(--blue);
    opacity: 1;
  }

  @keyframes tbc-slideUp {
    from { opacity: 0; transform: translateY(40px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes tbc-ticker {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }

  @keyframes tbc-reveal {
    from {
      opacity: 0;
      transform: translateY(34px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes tbc-layer-pulse {
    0%, 100% { opacity: 0.72; }
    50% { opacity: 1; }
  }

  .tbc-layer-grid,
  .tbc-layer-panel {
    animation: tbc-layer-pulse 7s ease-in-out infinite;
  }

  @supports (animation-timeline: view()) {
    .tbc-section-label,
    .tbc-card,
    .tbc-about-title,
    .tbc-about-text,
    .tbc-stats,
    .tbc-about-visual,
    .tbc-contact-title,
    .tbc-contact-desc,
    .tbc-contact-card {
      animation: tbc-reveal linear both;
      animation-timeline: view();
      animation-range: entry 0% cover 28%;
    }

    .tbc-card:nth-child(2n),
    .tbc-contact-card {
      animation-range: entry 6% cover 34%;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .tbc-root *,
    .tbc-root *::before,
    .tbc-root *::after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      scroll-behavior: auto !important;
      transition-duration: 0.001ms !important;
    }

    .tbc-hero-bg,
    .tbc-layer,
    .tbc-about-code,
    .tbc-about-visual::before,
    .tbc-about-visual::after,
    .tbc-contact::before {
      transform: none !important;
    }
  }

  @media (max-width: 1100px) {
    .tbc-services-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .tbc-card:nth-child(3n) { border-right: 1px solid var(--border); }
    .tbc-card:nth-child(2n) { border-right: none; }

    .tbc-contact-wrap {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 820px) {
    .tbc-nav {
      padding: 1.2rem;
    }

    .tbc-nav-links {
      display: none;
    }

    .tbc-nav-actions {
      gap: 0.5rem;
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .tbc-nav-cta {
      padding: 0.55rem 1rem;
      font-size: 0.72rem;
    }

    .tbc-hero,
    .tbc-section,
    .tbc-footer {
      padding-left: 1.2rem;
      padding-right: 1.2rem;
    }

    .tbc-hero-bottom,
    .tbc-about,
    .tbc-form-row {
      display: grid;
      grid-template-columns: 1fr;
    }

    .tbc-hero-bottom {
      align-items: start;
    }

    .tbc-services-grid {
      grid-template-columns: 1fr;
    }

    .tbc-card,
    .tbc-card:nth-child(3n),
    .tbc-card:nth-child(2n) {
      border-right: none;
    }

    .tbc-about-visual {
      height: auto;
      min-height: 300px;
    }

    .tbc-contact-card {
      padding: 1.2rem;
    }

    .tbc-footer {
      flex-direction: column;
      align-items: flex-start;
    }

    .tbc-footer-links {
      justify-content: flex-start;
    }

    .tbc-stats {
      grid-template-columns: 1fr;
      gap: 1.4rem;
    }
  }
`;

const services = [
  {
    num: '01',
    icon: '◻',
    name: 'Desarrollo Web',
    desc: 'Sitios y aplicaciones web de alto rendimiento con tecnologías modernas como React, Next.js y TypeScript.',
  },
  {
    num: '02',
    icon: '◈',
    name: 'Apps Móviles',
    desc: 'Aplicaciones nativas y cross-platform para iOS y Android con experiencias de usuario excepcionales.',
  },
  {
    num: '03',
    icon: '⬡',
    name: 'APIs & Backend',
    desc: 'Arquitecturas robustas, microservicios escalables y APIs RESTful o GraphQL bien documentadas.',
  },
  {
    num: '04',
    icon: '◉',
    name: 'Cloud & DevOps',
    desc: 'Infraestructura en AWS, GCP o Azure con CI/CD, contenedores y despliegue automatizado.',
  },
  {
    num: '05',
    icon: '◧',
    name: 'UI / UX Design',
    desc: 'Diseño de interfaces centradas en el usuario: wireframes, prototipos y sistemas de diseño completos.',
  },
  {
    num: '06',
    icon: '◬',
    name: 'Consultoría Tech',
    desc: 'Asesoría estratégica en arquitectura de software, selección de stack y transformación digital.',
  },
];

const stats = [
  { val: '15+', label: 'proyectos entregados' },
  { val: '98%', label: 'satisfacción del cliente' },
  { val: '5+', label: 'industrias atendidas' },
  { val: '3+', label: 'años de experiencia' },
];

const tickerItems = [
  'Desarrollo Web',
  'Apps Móviles',
  'APIs & Backend',
  'Cloud & DevOps',
  'UI / UX Design',
  'Consultoría Tech',
];

const LandingPage: React.FC<TableProps> = (_props: TableProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);
  useParallaxMotion(rootRef);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
  });

  const [isSending, setIsSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!document.getElementById('tbc-styles')) {
      const el = document.createElement('style');
      el.id = 'tbc-styles';
      el.textContent = styles;
      document.head.appendChild(el);
      styleRef.current = el;
    }

    return () => {
      styleRef.current?.remove();
    };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setErrorMessage('Por favor ingresa tu nombre.');
      return false;
    }

    if (!formData.email.trim()) {
      setErrorMessage('Por favor ingresa tu email.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Por favor ingresa un email válido.');
      return false;
    }

    if (!formData.description.trim()) {
      setErrorMessage('Por favor describe tu proyecto.');
      return false;
    }

    setErrorMessage('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    if (!validateForm()) return;

    setIsSending(true);

    debugger
    try {
      const payload = {
        Person: formData.name,
        Email: formData.email,
        Phone: formData.phone.trim() ? formData.phone : null,
        Description: formData.description,
      };

      const response = await _props.postSendEmailSolicitudBusinessProject(payload);

      if (response != undefined) {
        setSuccessMessage('Gracias. Tu solicitud fue enviada correctamente.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          description: '',
        });
      }

    } catch (error) {
      console.error('Error enviando formulario:', error);
      setErrorMessage('Ocurrió un error al enviar el formulario.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="tbc-root" ref={rootRef}>
      {/* NAV */}
      <nav className="tbc-nav">
        <Link to="/" className="tbc-logo">
          the<span>blue</span>coding
        </Link>

        <ul className="tbc-nav-links">
          <li><a href="#servicios">servicios</a></li>
          <li><a href="#nosotros">nosotros</a></li>
          <li><a href="#contacto">contacto</a></li>
        </ul>

        <div className="tbc-nav-actions">
          <Link
            to="/aklio"
            className="tbc-nav-cta tbc-nav-secondary"
          >
            AKLIO →
          </Link>

          <button
            className="tbc-nav-cta"
            onClick={() => {
              const section = document.getElementById('contacto');
              section?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            iniciar proyecto →
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section className="tbc-hero">
        <div className="tbc-parallax-stage" aria-hidden="true">
          <span className="tbc-layer tbc-layer-grid" />
          <span className="tbc-layer tbc-layer-panel" />
          <span className="tbc-layer tbc-layer-rail" />
        </div>
        <div className="tbc-hero-bg">{'</>'}</div>

        <div className="tbc-hero-label">Desarrollo de software · Ecuador</div>

        <h1 className="tbc-hero-title">
          Código que
          <br />
          <span className="tbc-accent">construye</span>
          <br />
          futuro.
        </h1>

        <div className="tbc-hero-bottom">
          <p className="tbc-hero-desc">
            Transformamos ideas complejas en soluciones digitales elegantes y
            escalables.
          </p>

          <a href="#servicios" className="tbc-hero-btn">
            Ver servicios →
          </a>
        </div>
      </section>

      {/* TICKER */}
      <div className="tbc-ticker-wrap">
        <div className="tbc-ticker">
          {[...tickerItems, ...tickerItems].map((item, i) => (
            <span key={i} className="tbc-ticker-item">
              {item} <span>·</span>
            </span>
          ))}
        </div>
      </div>

      {/* SERVICES */}
      <section id="servicios" className="tbc-section">
        <div className="tbc-section-label">Nuestros servicios</div>

        <div className="tbc-services-grid">
          {services.map((s) => (
            <div key={s.num} className="tbc-card">
              <span className="tbc-card-num">{s.num}</span>
              <span className="tbc-card-icon">{s.icon}</span>
              <h3 className="tbc-card-name">{s.name}</h3>
              <p className="tbc-card-desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section id="nosotros" className="tbc-section" style={{ paddingTop: 0 }}>
        <div className="tbc-about">
          <div>
            <div className="tbc-section-label">Sobre nosotros</div>

            <h2 className="tbc-about-title">
              Pensamos en <span className="tbc-accent">sistemas,</span> no solo
              en código.
            </h2>

            <p className="tbc-about-text">
              En thebluecoding combinamos rigor técnico con visión de producto.
              Trabajamos con startups y empresas que quieren construir
              soluciones duraderas, no parches temporales.
            </p>

            <div className="tbc-stats">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="tbc-stat-val">{s.val}</div>
                  <div className="tbc-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="tbc-about-visual">
            <pre className="tbc-about-code">
              <span className="tbc-kw">const </span>
              <span className="tbc-fn">buildProduct </span>
              {'= async (idea) => {\n'}
              {'  '}
              <span className="tbc-kw">const </span>
              {'plan = '}
              <span className="tbc-kw">await </span>
              {'understand(idea);\n'}
              {'  '}
              <span className="tbc-kw">const </span>
              {'design = craft(plan);\n'}
              {'  '}
              <span className="tbc-kw">const </span>
              {'code = engineer(design);\n'}
              {'  '}
              <span className="tbc-kw">return </span>
              {'deploy(code, '}
              <span className="tbc-str">{'\'production\''}</span>
              {');\n'}
              {'};\n\n'}
              <span className="tbc-str">{'// We ship. Every time.'}</span>
            </pre>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contacto" className="tbc-section tbc-contact">
        <div className="tbc-contact-wrap">
          <div>
            <div className="tbc-section-label">Contáctanos</div>

            <h2 className="tbc-contact-title">
              Hagamos algo
              <br />
              <span className="tbc-accent">increíble</span>
              <br />
              juntos.
            </h2>

            <p className="tbc-contact-desc">
              Cuéntanos qué necesitas y te responderemos para ayudarte a
              convertir tu idea en una solución digital clara, funcional y
              escalable.
            </p>
          </div>

          <div className="tbc-contact-card">
            <form className="tbc-contact-form" onSubmit={handleSubmit}>
              <div className="tbc-form-row">
                <div className="tbc-field">
                  <label htmlFor="name">Nombre</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    className="tbc-input"
                    placeholder="Tu nombre"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>

                <div className="tbc-field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="tbc-input"
                    placeholder="tunombre@email.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="tbc-field">
                <label htmlFor="phone">Teléfono (opcional)</label>
                <input
                  id="phone"
                  name="phone"
                  type="text"
                  className="tbc-input"
                  placeholder="+593 99 999 9999"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>

              <div className="tbc-field">
                <label htmlFor="description">Descripción del proyecto</label>
                <textarea
                  id="description"
                  name="description"
                  className="tbc-textarea"
                  placeholder="Cuéntanos brevemente qué deseas construir, mejorar o automatizar."
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>

              <div className="tbc-form-note">
                Los campos de nombre, email y descripción del proyecto son
                obligatorios.
              </div>

              <button type="submit" className="tbc-submit" disabled={isSending}>
                {isSending ? 'enviando...' : 'enviar solicitud →'}
              </button>

              {successMessage && (
                <div className="tbc-success">{successMessage}</div>
              )}

              {errorMessage && (
                <div className="tbc-error">{errorMessage}</div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="tbc-footer">
        <Link to="/" className="tbc-logo">
          the<span style={{ color: 'var(--blue)' }}>blue</span>coding
        </Link>

        <nav className="tbc-footer-links" aria-label="Documentos legales">
          <Link to="/aklio/legal/terminos-condiciones">
            T&eacute;rminos y condiciones
          </Link>
          <Link to="/aklio/legal/cookies">Pol&iacute;tica de cookies</Link>
          <Link to="/aklio/legal/privacidad-datos">
            Privacidad y datos
          </Link>
        </nav>

        <span className="tbc-copy">© 2025 thebluecoding — Ecuador</span>
      </footer>
    </div>
  );
};

const mapStateToProps = (_state: AppState) => ({

  
});

export default connect(mapStateToProps, {
  postSendEmailSolicitudBusinessProject
})(LandingPage);
